import { z } from 'zod';
import { unitPreferencesSchema } from './units.js';
import { featureKeySchema } from './features.js';
export const loginRequestSchema = z.object({
    email: z.string().email(),
    password: z.string().min(8),
});
export const tenantMembershipSchema = z.object({
    tenantId: z.string().uuid(),
    tenantName: z.string(),
    role: z.enum(['TENANT_ADMIN', 'OPERATIONS_MANAGER', 'OPERATOR', 'VIEWER']),
    /**
     * Which half of the hierarchy this membership is in.
     *
     * Present because a tenant administrator now holds TWO memberships — the
     * parent, where branding and entitlements live, and the sub-tenant, where
     * their vehicles are. The client has to know which is which to land them
     * somewhere useful; picking the first membership would drop them into an
     * empty management context and read as data loss.
     */
    tenantType: z.enum(['PARENT', 'SUB']),
    /**
     * The customer this tenant belongs to, or null for a parent.
     *
     * Needed because a sub-tenant the user is an EXPLICIT member of is excluded
     * from `subTenants` — so without it the client cannot tell that their
     * membership in "Default" belongs under "Halewood Transport", and the switcher lists the
     * two as unrelated peers.
     */
    parentTenantId: z.string().uuid().nullable(),
    /**
     * ACTIVE | SUSPENDED | ARCHIVED, as far as this session is concerned.
     *
     * A membership in a suspended tenant is still a membership — the person has
     * not lost anything — but nothing in it can be used. The client needs to tell
     * those apart to say something true rather than render an application in
     * which every request fails.
     */
    tenantState: z.enum(['ACTIVE', 'SUSPENDED', 'ARCHIVED']),
});
/**
 * A sub-tenant reachable through a PARENT membership rather than an explicit one.
 *
 * Present only for somebody who actually administers a parent (design D5): a
 * sub-tenant user must not learn the hierarchy exists, so for them this list is
 * empty and the client renders exactly what it rendered before.
 */
export const accessibleSubTenantSchema = z.object({
    tenantId: z.string().uuid(),
    tenantName: z.string(),
    parentTenantId: z.string().uuid(),
    /** Where the customer's existing fleet lives; it cannot be created or removed. */
    isDefault: z.boolean(),
    displayOrder: z.number().int().nullable(),
    /**
     * ACTIVE or SUSPENDED — never ARCHIVED, which is filtered out before this is
     * built. Carried for the same reason `tenantMembershipSchema` carries it: the
     * switcher has to decide whether this is somewhere the user can actually go,
     * and it cannot apply a rule whose input it was never sent.
     */
    tenantState: z.enum(['ACTIVE', 'SUSPENDED']),
});
/**
 * A white-label partner the session administers, with what lies beneath it.
 *
 * Nested rather than flattened into `subTenants`, because the switcher groups by
 * customer and a fleet account reached through a partner still belongs under its
 * COMPANY — flattening would lose which company each fleet account is in, which is
 * the only structure that makes a list of forty fleet accounts readable.
 */
export const accessibleWhiteLabelSchema = z.object({
    whiteLabelPartnerId: z.string().uuid(),
    code: z.string(),
    displayName: z.string(),
    /**
     * What this session may DO in the partner console.
     *
     * ⚠️ Defaulted to `WL_ADMIN` so an OLDER SERVER that does not send it keeps behaving
     * exactly as it did — before this field, only administrators were ever listed here, so
     * an absent value can only have meant one. Defaulting the other way would strip every
     * write control from a working console the moment a new bundle met an old API.
     *
     * ⚠️ A DISPLAY hint, never an authorization input. Every non-GET from a viewer is refused
     * by `WhiteLabelContextGuard`, so a client lying about this only shows itself buttons
     * that 403.
     */
    role: z.enum(['WL_ADMIN', 'WL_VIEWER']).default('WL_ADMIN'),
    /**
     * The RESELLER this session administers, or null for the whole partner.
     *
     * ⚠️ Sent because **the client cannot apply a rule whose input it was never given** —
     * the reason `tenantMembershipDto` carries `tenantState`. Every partner nav item is
     * flagged `whiteLabel: true`, so without this a reseller administrator is offered Brand,
     * Regional defaults, People and Resellers, and all four 403 when pressed: the defect
     * `isNavItemVisible` exists to prevent, arriving one level down.
     *
     * ⚠️ A DISPLAY hint, never an authorization input. The server resolves the level from
     * the session's own membership on every request (D15); a client that lied about this
     * would hide its own menu items and gain nothing.
     */
    resellerId: z.string().uuid().nullable(),
    /** For titling the console. Null at partner level. */
    resellerName: z.string().nullable(),
    companies: z.array(z.object({
        tenantId: z.string().uuid(),
        tenantName: z.string(),
        /** ACTIVE or SUSPENDED — never ARCHIVED, filtered out before this is built. */
        tenantState: z.enum(['ACTIVE', 'SUSPENDED']),
        fleetAccounts: z.array(z.object({
            tenantId: z.string().uuid(),
            tenantName: z.string(),
            tenantState: z.enum(['ACTIVE', 'SUSPENDED']),
        })),
    })),
});
export const authUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    displayName: z.string(),
    isPlatformAdmin: z.boolean(),
    memberships: z.array(tenantMembershipSchema),
    /**
     * Sub-tenants reachable by derived role, EXCLUDING any the user holds an
     * explicit membership in — those are already in `memberships`, and listing a
     * tenant twice would make the switcher offer two entries for one place.
     */
    subTenants: z.array(accessibleSubTenantSchema),
    /**
     * Companies and fleet accounts reachable because the user administers a
     * WHITE-LABEL PARTNER (`white-label-partner-tier-design.md` D13).
     *
     * Needed because a partner administrator holds **no tenant membership at
     * all** — so `memberships` and `subTenants` are both empty for them, and
     * without this the tenant chooser would have nothing to offer somebody who
     * can reach dozens of organisations.
     *
     * Empty for everybody who administers no partner, so the client renders
     * exactly what it rendered before. Same posture as `subTenants`: a company
     * user must not learn that a partner tier exists.
     */
    whiteLabels: z.array(accessibleWhiteLabelSchema),
});
/**
 * Tokens returned by login and by refresh.
 *
 * Two credentials with deliberately different lifetimes. The access token stays
 * short — an hour — because it is presented on every request and cannot be
 * revoked individually; the refresh token is long, single-use, and revocable,
 * so a session survives a working day without a stolen bearer token doing the
 * same.
 *
 * The refresh token is returned in the BODY rather than set as a cookie. That
 * is a deliberate trade: a cookie would resist XSS better on the web, but a
 * native client cannot use one, and this API is Bearer-based throughout. The
 * mitigations are rotation on every use and family revocation on reuse — see
 * `RefreshTokenRepository`. A cookie variant remains available for the web
 * alone if that balance ever needs revisiting.
 */
export const tokenPairSchema = z.object({
    accessToken: z.string(),
    /** Seconds until `accessToken` expires, so a client can renew before failing. */
    expiresIn: z.number().int().positive(),
    refreshToken: z.string(),
    refreshExpiresAt: z.string().datetime(),
});
export const loginResponseSchema = tokenPairSchema.extend({
    user: authUserSchema,
});
export const refreshRequestSchema = z.object({ refreshToken: z.string().min(1) }).strict();
export const refreshResponseSchema = tokenPairSchema;
export const logoutRequestSchema = z
    .object({
    /** Omitted is allowed: signing out without one still ends the access token. */
    refreshToken: z.string().min(1).optional(),
    /** Revoke every session for this user, not just this device. */
    allDevices: z.boolean().optional(),
})
    .strict();
export const meResponseSchema = authUserSchema.extend({
    activeTenantId: z.string().uuid().nullable(),
    /**
     * Display units for the active tenant.
     *
     * Carried HERE rather than only on the admin settings endpoint, which is
     * TENANT_ADMIN-only: a viewer must see the same units as the administrator
     * who chose them, and any other arrangement means the same figure reads
     * differently depending on who is looking at it.
     *
     * Defaults when there is no active tenant — a platform administrator who has
     * not entered one still has to render numbers.
     */
    units: unitPreferencesSchema,
    /**
     * Features enabled for the active tenant.
     *
     * Carried here for the same reason units are: every role needs it, and the
     * platform endpoint that manages entitlements is platform-scoped. This drives
     * navigation only — the server guards each route independently, because
     * hiding a link has never stopped anyone typing a URL.
     */
    features: z.array(featureKeySchema),
});
// ── password management ──────────────────────────────────────────────────────
/**
 * Password policy for NEW passwords.
 *
 * The 72-byte ceiling is not arbitrary: bcrypt hashes only the first 72 bytes
 * and silently discards the rest, so without this cap two different long
 * passwords sharing a 72-byte prefix would authenticate interchangeably.
 * Rejecting them is honest; truncating them is not.
 *
 * Length is measured in BYTES, not characters, because that is what bcrypt
 * counts — a 30-character password of multi-byte glyphs can exceed 72 bytes.
 */
export const NEW_PASSWORD_MIN_LENGTH = 12;
export const BCRYPT_MAX_BYTES = 72;
export const newPasswordSchema = z
    .string()
    .min(NEW_PASSWORD_MIN_LENGTH, `Password must be at least ${NEW_PASSWORD_MIN_LENGTH} characters`)
    .refine((v) => Buffer.byteLength(v, 'utf8') <= BCRYPT_MAX_BYTES, {
    message: `Password must be at most ${BCRYPT_MAX_BYTES} bytes`,
});
/**
 * Change the authenticated user's own password.
 *
 * There is deliberately no `userId` field: the subject is always the caller.
 * Changing someone else's password is a different operation with a different
 * threat model, and `.strict()` makes an attempt to supply one a 400 rather
 * than a silently ignored field.
 *
 * `currentPassword` is required even though the caller is already
 * authenticated: it is what stops a stolen or borrowed session from taking
 * permanent ownership of the account.
 */
export const changePasswordRequestSchema = z
    .object({
    currentPassword: z.string().min(1),
    newPassword: newPasswordSchema,
})
    .strict()
    .refine((v) => v.currentPassword !== v.newPassword, {
    message: 'New password must differ from the current password',
    path: ['newPassword'],
});
/**
 * A successful change invalidates every outstanding token for the user —
 * including the one used to make the request — so the client must
 * re-authenticate. No token is returned.
 */
export const changePasswordResponseSchema = z.object({
    passwordChanged: z.literal(true),
    reauthenticationRequired: z.literal(true),
});
/**
 * JWT payload shape — kept minimal; tenant is resolved per-request.
 *
 * `tv` is the token version captured at issue time. It is compared against
 * `User.tokenVersion` on every authenticated request, so incrementing the
 * database value invalidates every outstanding token immediately (ADR 0008).
 *
 * `isPlatformAdmin` remains in the payload for display/telemetry only. It is
 * NEVER the basis of an authorization decision — `PlatformAdminGuard` re-reads
 * the flag from the database. A forged or stale claim therefore grants nothing.
 */
export const jwtPayloadSchema = z.object({
    sub: z.string().uuid(),
    email: z.string().email(),
    isPlatformAdmin: z.boolean(),
    tv: z.number().int().nonnegative(),
});
//# sourceMappingURL=auth.js.map